/**
 * @fileoverview Input/output schemas for plot-planner-v1.
 *
 * This agent takes the structural output from storylines-planner-v1 (storylines,
 * beats, story questions) plus story context, and produces a detailed written
 * plot narrative in Markdown format.
 *
 * It is the "Plot Planner" in the split-planner pipeline:
 *   1. storylines-planner-v1 → structural output
 *   2. plot-planner-v1 → writtenPlot from structure (this agent)
 *   3. character-ideation-v2 → suggestedCharacters from structure
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  FlowSourceMaterialSchema,
  type FlowSourceMaterial,
} from '../storylines-planner-v1/schema';

// Re-export source material types for consumer convenience
export type { FlowSourceMaterial };
export { FlowSourceMaterialSchema };

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .merge(AGENT_SCHEMA_OBJECTS.existingStorylines.schema)
  .merge(z.object({
    sourceMaterials: z.array(FlowSourceMaterialSchema).optional().describe(
      'Source stories for sequel/inspiration-based plots with pre-computed headers.'
    ),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  summary: z.string().describe(
    'A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict. ' +
    'This summary is displayed as a subtitle on the Active Story Outline card and used as context by other AI agents.'
  ),
  writtenPlot: z.string().describe(
    'A detailed, multi-paragraph plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Weave together all storylines into a cohesive narrative covering every phase. ' +
    'The narrative should be verbose and granular enough to guide subsequent chapter planning and prose drafting.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
